import { HAND_DELIVERY_SERVICE } from "@erp/shared";
import { CARRIER_VALUES, SHIPMENT_KIND_VALUES, SHIPMENT_SORT_KEYS, SHIPMENT_STATUS_VALUES } from "@erp/shared";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Package, PackageMinus } from "lucide-react";
import { useCallback } from "react";
import { z } from "zod";
import { Badge } from "~/components/ui/badge";
import { buttonVariants } from "~/components/ui/button";
import { DataList, type Column } from "~/components/ui/data-list";
import { Page, PageHeader, Panel, SearchInput, Segmented, Toolbar } from "~/components/ui/page";
import { Input } from "~/components/ui/input";
import { Select } from "~/components/ui/select";
import { EmptyState, ErrorState } from "~/components/ui/states";
import { SHIPMENT_TONE } from "~/features/sales/status";
import { api, unwrap } from "~/lib/api";
import { useFormat, useT } from "~/lib/i18n";
import { useCan } from "~/lib/session";
import { usePagedList } from "~/lib/use-list";
import { Pagination } from "~/components/ui/pagination";
import { HelpTip } from "~/components/ui/help-tip";

const KINDS = SHIPMENT_KIND_VALUES;
const ALL = "all";

const isoDate = z.iso.date();
const search = z.object({
  kind: z.enum(KINDS).optional(),
  q: z.string().optional(),
  status: z.enum(SHIPMENT_STATUS_VALUES).optional(),
  carrier: z.enum(CARRIER_VALUES).optional(),
  from: isoDate.optional(),
  to: isoDate.optional(),
  sort: z.enum(SHIPMENT_SORT_KEYS).optional(),
  dir: z.enum(["asc", "desc"]).optional(),
  page: z.coerce.number().int().min(1).optional(),
});
type Search = z.infer<typeof search>;

const PAGE_SIZE = 50;

export const Route = createFileRoute("/_app/shipments/")({ validateSearch: search, component: Shipments });

const fetchShipments = (params: Omit<Search, "page"> & { offset: number; limit: number }) =>
  unwrap(api.shipments.get({ query: { ...params, dir: params.dir ?? "desc" } }));
type Row = Awaited<ReturnType<typeof fetchShipments>>["items"][number];

function Shipments() {
  const t = useT();
  const format = useFormat();
  const navigate = useNavigate({ from: Route.fullPath });
  const filters = Route.useSearch();
  const can = useCan();
  const { page: pageParam, ...query } = filters;
  const page = pageParam ?? 1;
  const list = usePagedList("shipments", query, page, PAGE_SIZE, fetchShipments);
  // A new search, filter or sort starts again from the first page.
  const setSearch = useCallback(
    (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, page: undefined, ...patch }), replace: true }),
    [navigate],
  );
  const setKind = useCallback(
    (kind: (typeof KINDS)[number] | typeof ALL) => setSearch({ kind: kind === ALL ? undefined : kind }),
    [setSearch],
  );

  const date = (r: Row) => format.date(r.shippedAt ?? r.createdAt, "short");
  const columns: Column<Row>[] = [
    {
      id: "number",
      header: t("shipments.fields.number"),
      sortKey: "number",
      cell: (r) => (
        <div className="py-1.5">
          <p className="font-medium tabular">{r.number}</p>
          <p className="text-xs text-fg-3">{t(`shipments.kinds.${r.kind}`)}</p>
        </div>
      ),
    },
    {
      id: "recipient",
      header: t("shipments.fields.recipient"),
      sortKey: "recipient",
      cell: (r) => (
        <div className="min-w-0 py-1.5">
          <p className="truncate">{r.recipientName}</p>
          {r.orderNumber ? <p className="text-xs text-fg-3 tabular">{r.orderNumber}</p> : null}
        </div>
      ),
      className: "max-w-[280px]",
    },
    { id: "date", header: t("shipments.fields.date"), sortKey: "date", cell: date, className: "text-fg-2" },
    {
      id: "carrier",
      header: t("shipments.fields.carrier"),
      sortKey: "carrier",
      cell: (r) => (r.service === HAND_DELIVERY_SERVICE ? t("shipments.handDelivery") : t(`shipments.carriers.${r.carrier}`)),
      className: "text-fg-2",
      minWidth: "xl",
    },
    {
      id: "tracking",
      header: t("shipments.fields.tracking"),
      sortKey: "tracking",
      cell: (r) => <span className="tabular">{r.trackingNumber ?? ""}</span>,
      minWidth: "lg",
    },
    {
      id: "status",
      header: t("common.fields.status"),
      sortKey: "status",
      cell: (r) => <Badge tone={SHIPMENT_TONE[r.status]}>{t(`shipments.statuses.${r.status}`)}</Badge>,
    },
  ];

  return (
    <Page>
      <PageHeader
        title={t("shipments.title")}
        actions={
          can("stock:write") ? (
            <span className="inline-flex items-center gap-0.5">
              <Link to="/shipments/free" className={buttonVariants({ variant: "secondary" })}>
                <PackageMinus />
                {t("shipments.free.action")}
              </Link>
              <HelpTip id="shipment.free" />
            </span>
          ) : null
        }
      />
      <Panel>
        <Toolbar>
          <div className="w-full min-w-0">
            <Segmented
              label={t("shipments.title")}
              value={filters.kind ?? ALL}
              onChange={setKind}
              options={[
                { value: ALL, label: t("shipments.views.all") },
                ...KINDS.map((k) => ({ value: k, label: t(`shipments.kinds.${k}`) })),
              ]}
            />
          </div>
          <SearchInput
            value={filters.q ?? ""}
            onChange={(q) => setSearch({ q: q || undefined })}
            placeholder={t("shipments.searchPlaceholder")}
          />
          <div className="grid w-full grid-cols-2 gap-2 sm:w-auto sm:grid-cols-[170px_170px_150px_150px]">
            <Select
              aria-label={t("common.fields.status")}
              value={filters.status ?? ALL}
              onValueChange={(v) => setSearch({ status: v === ALL ? undefined : (v as Search["status"]) })}
              options={[
                { value: ALL, label: t("shipments.filters.allStatuses") },
                ...SHIPMENT_STATUS_VALUES.map((v) => ({ value: v, label: t(`shipments.statuses.${v}`) })),
              ]}
            />
            <Select
              aria-label={t("shipments.fields.carrier")}
              value={filters.carrier ?? ALL}
              onValueChange={(v) => setSearch({ carrier: v === ALL ? undefined : (v as Search["carrier"]) })}
              options={[
                { value: ALL, label: t("shipments.filters.allCarriers") },
                ...CARRIER_VALUES.map((v) => ({ value: v, label: t(`shipments.carriers.${v}`) })),
              ]}
            />
            <Input
              type="date"
              aria-label={t("shipments.filters.from")}
              value={filters.from ?? ""}
              onChange={(e) => setSearch({ from: e.target.value || undefined })}
            />
            <Input
              type="date"
              aria-label={t("shipments.filters.to")}
              value={filters.to ?? ""}
              onChange={(e) => setSearch({ to: e.target.value || undefined })}
            />
          </div>
        </Toolbar>
        {list.isError ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : (
          <DataList
            rows={list.rows}
            rowKey={(r) => r.id}
            columns={columns}
            card={(r) => (
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate font-medium">{r.recipientName}</p>
                  <p className="text-xs text-fg-3 tabular">
                    {r.number} — {date(r)} — {t(`shipments.kinds.${r.kind}`)}
                  </p>
                  {r.trackingNumber ? <p className="mt-0.5 text-xs text-fg-2 tabular">{r.trackingNumber}</p> : null}
                </div>
                <Badge tone={SHIPMENT_TONE[r.status]}>{t(`shipments.statuses.${r.status}`)}</Badge>
              </div>
            )}
            onOpen={(r) => void navigate({ to: "/shipments/$id", params: { id: r.id } })}
            sort={{ key: filters.sort ?? "date", dir: filters.dir ?? "desc" }}
            onSortChange={(s) => setSearch({ sort: s.key as Search["sort"], dir: s.dir })}
            loading={list.isLoading}
            hasMore={false}
            loadingMore={false}
            onLoadMore={() => undefined}
            isDimmed={(r) => r.status === "cancelled"}
            empty={<EmptyState icon={<Package />} title={t("shipments.empty.title")} body={t("shipments.empty.body")} />}
          />
        )}
        <Pagination
          page={page}
          pageSize={PAGE_SIZE}
          total={list.total}
          onPageChange={(next) => {
            void navigate({ search: (prev) => ({ ...prev, page: next > 1 ? next : undefined }) });
            window.scrollTo({ top: 0 });
          }}
        />
      </Panel>
    </Page>
  );
}
